import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, PlayCircle, Plus, Trash, CheckCircle, Warning, XCircle,
  ArrowsClockwise, Spinner, X, Hash, ArrowUp, Buildings
} from "@phosphor-icons/react";

const STATUS_META = {
  STABLE: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
  DRIFTED: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: Warning },
  FAILED: { color: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
};

function StatusPill({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.STABLE;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs px-2 py-0.5 border ${m.color}`} data-testid={testid}>
      <Icon size={10} weight="fill" />
      {status}
    </span>
  );
}

export default function GoldenQuestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAll, setRunningAll] = useState(false);
  const [newQuery, setNewQuery] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [runningOne, setRunningOne] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/golden");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newQuery.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/golden", {
        query: newQuery.trim(),
        expected_keywords: newKeywords
          .split(",")
          .map((k) => k.trim())
          .filter(Boolean),
      });
      toast.success(`Baseline captured · ${data.latest_status}`);
      setNewQuery("");
      setNewKeywords("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const runAll = async () => {
    setRunningAll(true);
    try {
      const { data } = await api.post("/golden/run-all");
      toast.success(`Run complete · ${data.stable} stable · ${data.drifted} drifted · ${data.failed} failed`);
      load();
    } catch {
      toast.error("Run failed");
    } finally {
      setRunningAll(false);
    }
  };

  const runOne = async (id) => {
    setRunningOne(true);
    try {
      await api.post(`/golden/${id}/run`);
      const { data } = await api.get("/golden");
      setItems(data);
      toast.success("Run complete");
    } catch {
      toast.error("Run failed");
    } finally {
      setRunningOne(false);
    }
  };

  const remove = async (id, query) => {
    if (!window.confirm(`Delete Golden Question "${query.slice(0, 60)}…"?`)) return;
    try {
      await api.delete(`/golden/${id}`);
      toast.success("Deleted");
      load();
      if (openId === id) setOpenId(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const promote = async (id) => {
    if (!window.confirm("Promote today's answer as the new baseline? Future runs will compare against this.")) return;
    try {
      await api.post(`/golden/${id}/promote`);
      toast.success("Baseline updated");
      load();
    } catch {
      toast.error("Promote failed");
    }
  };

  const detail = useMemo(() => items.find((it) => it.id === openId), [items, openId]);

  const counts = useMemo(() => {
    const c = { stable: 0, drifted: 0, failed: 0 };
    for (const it of items) {
      if (it.latest_status === "STABLE") c.stable++;
      else if (it.latest_status === "DRIFTED") c.drifted++;
      else if (it.latest_status === "FAILED") c.failed++;
    }
    return c;
  }, [items]);

  return (
    <div className="space-y-8" data-testid="golden-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// regression · drift detection</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Golden Questions
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          A pinned suite of expected-answer queries. Run them after every knowledge change to see which
          user-facing answers just drifted — before your users do.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total" value={items.length} />
        <Metric label="Stable" value={counts.stable} tone="emerald" />
        <Metric label="Drifted" value={counts.drifted} tone="orange" />
        <Metric label="Failed" value={counts.failed} tone="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create form */}
        <form onSubmit={create} className="border border-slate-200 bg-white p-6 space-y-4" data-testid="golden-form">
          <div className="mono-label">Pin a new Golden Question</div>
          <div>
            <label className="mono-label block mb-1">Query</label>
            <textarea
              data-testid="golden-query-input"
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
              rows={3}
              placeholder="e.g. What is the MRP of Aashirvaad Multigrain Atta 5kg?"
              className="w-full border border-slate-300 px-3 py-2 outline-none focus:border-slate-900 text-sm resize-none"
              style={{ borderRadius: 2 }}
              required
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Expected keywords (comma separated, optional)</label>
            <input
              data-testid="golden-keywords-input"
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
              placeholder="385, 5kg, Munger"
              className="w-full border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            />
            <div className="mono-label mt-1 text-slate-400">
              The answer must contain ALL keywords. Missing keyword → <span className="text-rose-700">FAILED</span>.
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            className="w-full btn-sharp btn-primary disabled:opacity-50"
            data-testid="golden-create-btn"
          >
            {creating ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
            {creating ? "Capturing baseline…" : "Pin & capture baseline"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-2 border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="mono-label flex items-center gap-2">
              <Trophy size={14} className="text-orange-600" />
              Suite
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} className="btn-sharp btn-secondary text-xs" data-testid="golden-refresh">
                <ArrowsClockwise size={12} />
              </button>
              <button
                onClick={runAll}
                disabled={runningAll || items.length === 0}
                className="btn-sharp btn-primary text-xs disabled:opacity-50"
                data-testid="golden-run-all-btn"
              >
                {runningAll ? <Spinner size={12} className="animate-spin" /> : <PlayCircle size={12} weight="fill" />}
                {runningAll ? "Running…" : "Run all"}
              </button>
            </div>
          </div>
          {loading ? (
            <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">
              No Golden Questions yet. Pin your first one with the form on the left.
            </div>
          ) : (
            <div data-testid="golden-list">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="border-b border-slate-100 px-5 py-3 hover:bg-slate-50 transition-all flex items-start gap-3"
                  data-testid={`golden-row-${it.id}`}
                >
                  <StatusPill status={it.latest_status} testid={`golden-status-${it.id}`} />
                  <button
                    onClick={() => setOpenId(it.id)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`golden-open-${it.id}`}
                  >
                    <div className="text-sm text-slate-900 truncate">{it.query}</div>
                    <div className="mono-label truncate">
                      {it.expected_keywords?.length
                        ? `kws: ${it.expected_keywords.join(", ")}`
                        : "no keywords"}
                      · {it.run_count} run{it.run_count !== 1 ? "s" : ""}
                      {it.last_run_at && <span> · {new Date(it.last_run_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                  </button>
                  <span className="shrink-0 font-mono text-xs px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
                    ₹{(it.latest?.cost_inr || 0).toFixed(4)}
                  </span>
                  <button
                    onClick={() => runOne(it.id)}
                    disabled={runningOne}
                    className="text-slate-400 hover:text-orange-600 disabled:opacity-50"
                    data-testid={`golden-run-${it.id}`}
                    title="Run now"
                  >
                    <PlayCircle size={16} weight="fill" />
                  </button>
                  <button
                    onClick={() => remove(it.id, it.query)}
                    className="text-slate-400 hover:text-orange-600"
                    data-testid={`golden-delete-${it.id}`}
                    title="Delete"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={() => setOpenId(null)} data-testid="golden-drawer">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl bg-white h-full overflow-y-auto border-l border-slate-200"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="mono-label text-orange-600">// golden detail</div>
                <div className="font-mono text-xs text-slate-500 mt-1">{detail.id}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => runOne(detail.id)}
                  disabled={runningOne}
                  className="btn-sharp btn-secondary text-sm disabled:opacity-50"
                  data-testid="golden-drawer-run"
                >
                  {runningOne ? <Spinner size={14} className="animate-spin" /> : <PlayCircle size={14} weight="fill" />}
                  Run now
                </button>
                <button
                  onClick={() => promote(detail.id)}
                  disabled={detail.latest_status === "STABLE"}
                  className="btn-sharp btn-primary text-sm disabled:opacity-50"
                  title={detail.latest_status === "STABLE" ? "Already aligned with baseline" : "Accept latest as new baseline"}
                  data-testid="golden-drawer-promote"
                >
                  <ArrowUp size={14} weight="bold" />
                  Promote as baseline
                </button>
                <button onClick={() => setOpenId(null)} className="btn-sharp btn-ghost" data-testid="golden-drawer-close">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <div className="mono-label mb-2">Query</div>
                <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 font-mono">
                  {detail.query}
                </div>
                {detail.expected_keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid="golden-keywords">
                    <span className="mono-label">expected:</span>
                    {detail.expected_keywords.map((k) => (
                      <span key={k} className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200">
                        <Hash size={10} className="inline mr-1" />
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Current status */}
              <div className="flex items-center gap-3 flex-wrap">
                <StatusPill status={detail.latest_status} />
                <span className="mono-label">last run:</span>
                <span className="font-mono text-xs text-slate-700">
                  {detail.last_run_at ? new Date(detail.last_run_at).toLocaleString() : "—"}
                </span>
                <span className="mono-label">runs:</span>
                <span className="font-mono text-xs text-slate-700">{detail.run_count}</span>
              </div>

              {/* Keyword check */}
              {detail.latest_diff && detail.expected_keywords?.length > 0 && (
                <div>
                  <div className="mono-label mb-2">Keyword check</div>
                  <div className="flex flex-wrap gap-1">
                    {(detail.latest_diff.keyword_hits || []).map((k) => (
                      <span key={`h-${k}`} className="font-mono text-xs px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800">
                        ✓ {k}
                      </span>
                    ))}
                    {(detail.latest_diff.keyword_missing || []).map((k) => (
                      <span key={`m-${k}`} className="font-mono text-xs px-2 py-0.5 border border-rose-200 bg-rose-50 text-rose-800">
                        ✗ {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Side-by-side baseline / latest */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Side title="Baseline" data={detail.baseline} testid="gq-baseline" />
                <Side title="Latest run" data={detail.latest} testid="gq-latest" />
              </div>

              {/* Citation diff */}
              {detail.latest_diff && (
                <div className="border border-slate-200 bg-white">
                  <div className="px-5 py-3 border-b border-slate-200 mono-label">Citation set diff</div>
                  <div className="p-5 space-y-1 text-xs">
                    {(detail.latest_diff.citations_kept || []).map((kid) => (
                      <CiteRow key={`k-${kid}`} label="KEPT" klass="bg-slate-100 text-slate-700" kid={kid} citations={[...(detail.baseline?.citations || []), ...(detail.latest?.citations || [])]} />
                    ))}
                    {(detail.latest_diff.citations_added || []).map((kid) => (
                      <CiteRow key={`a-${kid}`} label="ADDED" klass="bg-emerald-600 text-white" kid={kid} citations={[...(detail.baseline?.citations || []), ...(detail.latest?.citations || [])]} />
                    ))}
                    {(detail.latest_diff.citations_removed || []).map((kid) => (
                      <CiteRow key={`r-${kid}`} label="REMOVED" klass="bg-orange-600 text-white" kid={kid} citations={[...(detail.baseline?.citations || []), ...(detail.latest?.citations || [])]} />
                    ))}
                    {(detail.latest_diff.citations_kept || []).length === 0 &&
                      (detail.latest_diff.citations_added || []).length === 0 &&
                      (detail.latest_diff.citations_removed || []).length === 0 && (
                        <div className="text-slate-400">no citations</div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = "slate" }) {
  const color =
    tone === "emerald" ? "text-emerald-700"
      : tone === "orange" ? "text-orange-700"
      : tone === "rose" ? "text-rose-700"
      : "text-slate-900";
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label">{label}</div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Side({ title, data, testid }) {
  if (!data) {
    return (
      <div className="border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center mono-label" data-testid={testid}>
        not yet run
      </div>
    );
  }
  return (
    <div className="border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <div className="mono-label text-slate-900">{title}</div>
        <span className="ml-auto font-mono text-xs text-slate-500">
          {data.latency_ms}ms · ₹{(data.cost_inr || 0).toFixed(4)}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="text-sm text-slate-900 leading-relaxed whitespace-pre-wrap font-mono">
          {data.answer || "(no answer)"}
        </div>
        {(data.citations || []).length > 0 && (
          <div>
            <div className="mono-label mb-1">Citations ({(data.citations || []).length})</div>
            <div className="space-y-1">
              {data.citations.map((c, ci) => (
                <div key={ci} className="border border-slate-200 px-3 py-2 bg-slate-50 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-mono px-1.5 py-0.5 bg-slate-900 text-white">[{ci + 1}]</span>
                    <Buildings size={12} className="text-slate-500" />
                    <span className="font-mono text-slate-700 truncate flex-1">{c.location_path}</span>
                    <span className="font-mono text-orange-600">score {Number(c.score).toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-slate-900 font-medium">{c.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CiteRow({ label, klass, kid, citations }) {
  const title = citations.find((c) => c.knowledge_id === kid)?.title || kid.slice(0, 8);
  return (
    <div className="flex items-center gap-2">
      <span className={`font-mono text-xs px-1.5 py-0.5 w-20 text-center ${klass}`}>{label}</span>
      <span className="font-mono text-slate-700 truncate">{title}</span>
    </div>
  );
}
